"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useJobWizardStore } from "@/features/jobs/store/job-store";
import { useAtsConnections } from "@/features/ats-sync/hooks/use-ats-connections";
import { useAtsRemoteJobs } from "@/features/ats-sync/hooks/use-ats-remote-jobs";
import { useConnectionSyncSettings } from "@/features/ats-sync/hooks/use-sync-settings";
import {
  formatAtsJobLabel,
  isRemoteJobSelectable,
  remoteJobClosedBadge,
} from "@/features/ats-sync/lib/ats-job-picker";
import { useConnectorLogo } from "@/features/jobs/hooks/use-connector-logo";
import { Icon } from "@/components/ui/icon";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";

function ConnectionLogo({ connectorId }: { connectorId: string }) {
  const logo = useConnectorLogo(connectorId);
  if (logo) {
    return (
      <Image
        src={logo}
        alt=""
        width={16}
        height={16}
        className="size-4 shrink-0 rounded-[3px] object-cover"
      />
    );
  }
  return <Icon name="IconPuzzle" size={14} className="shrink-0 text-contrast/50" />;
}

/**
 * Connection + remote-job pickers for the "Import with an ATS" job-create path.
 * Writes the selection into the wizard form (connectionId / remoteJobId); the
 * wizard's create step calls the import endpoint, which creates the Arbi job
 * from the remote job and pulls its candidates.
 */
export function AtsImportFields() {
  const { form, updateForm } = useJobWizardStore();
  const { connections } = useAtsConnections();
  const active = connections.filter((c) => c.status === "active");
  const [jobOpen, setJobOpen] = useState(false);
  const [jobSearch, setJobSearch] = useState("");
  const [selectedJobMeta, setSelectedJobMeta] = useState<{
    title: string;
    remote_job_id: string;
  } | null>(null);
  const { showJobIdsInPicker } = useConnectionSyncSettings(form.connectionId);
  const {
    jobs,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    syncPending,
    syncPendingTimedOut,
  } = useAtsRemoteJobs(form.connectionId, jobSearch);
  const jobLabel = (job: { title: string; remote_job_id: string }) =>
    formatAtsJobLabel(job.title, job.remote_job_id, showJobIdsInPicker);
  const jobsViewportRef = useRef<HTMLDivElement>(null);
  useInfiniteScroll({
    rootRef: jobsViewportRef,
    hasNextPage: !!hasNextPage,
    isFetching: isFetchingNextPage,
    onLoadMore: fetchNextPage,
    enabled: jobOpen,
  });

  // Auto-select the first active connection when none is chosen yet.
  useEffect(() => {
    if (!form.connectionId && active.length > 0) {
      updateForm("connectionId", active[0].id);
    }
  }, [active, form.connectionId, updateForm]);

  useEffect(() => {
    setSelectedJobMeta(null);
  }, [form.connectionId]);

  useEffect(() => {
    if (!form.remoteJobId) setSelectedJobMeta(null);
  }, [form.remoteJobId]);

  if (active.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-contrast/[0.02] p-4 text-sm text-contrast/70 leading-normal">
        No ATS is connected yet. Connect one in{" "}
        <Link
          href="/settings/integrations/connections"
          className="text-primary underline"
        >
          Settings → Integrations
        </Link>{" "}
        to import a job and its candidates.
      </div>
    );
  }

  const selectedConn = active.find((c) => c.id === form.connectionId) ?? null;
  const listedSelectedJob =
    jobs.find((j) => j.remote_job_id === form.remoteJobId) ?? null;
  const selectedJob =
    listedSelectedJob ??
    (selectedJobMeta?.remote_job_id === form.remoteJobId
      ? selectedJobMeta
      : null);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <Label className="gap-0.5">
          ATS connection<span className="text-destructive">*</span>
        </Label>
        <Select.Root>
          <Select.Trigger active={!!selectedConn}>
            <Select.TriggerContent>
              <span className="flex items-center gap-2">
                {selectedConn && (
                  <ConnectionLogo connectorId={selectedConn.connector_id} />
                )}
                {selectedConn?.connector_name ?? "Select a connection…"}
              </span>
            </Select.TriggerContent>
            <Select.Icon />
          </Select.Trigger>
          <Select.Content>
            <Select.List>
              {active.map((c) => (
                <Select.Item
                  key={c.id}
                  selected={c.id === form.connectionId}
                  onSelect={() => {
                    updateForm("connectionId", c.id);
                    updateForm("remoteJobId", null);
                    updateForm("description", "");
                  }}
                >
                  <span className="flex items-center gap-2">
                    <ConnectionLogo connectorId={c.connector_id} />
                    <Select.ItemLabel>{c.connector_name}</Select.ItemLabel>
                  </span>
                  {c.id === form.connectionId && <Select.ItemCheck />}
                </Select.Item>
              ))}
            </Select.List>
          </Select.Content>
        </Select.Root>
      </div>

      <div className="flex flex-col gap-0.5">
        <Label className="gap-0.5">
          Job to import<span className="text-destructive">*</span>
          {syncPending && (
            <span className="ml-2 inline-flex items-center gap-1.5 text-xs font-normal text-contrast/55">
              <span className="ai-ping-dot" />
              {syncPendingTimedOut
                ? "Still syncing, try again later"
                : "Syncing"}
            </span>
          )}
        </Label>
        <Select.Root
          open={jobOpen}
          onOpenChange={(nextOpen) => {
            setJobOpen(nextOpen);
            if (nextOpen) setJobSearch("");
          }}
        >
          <Select.Trigger
            active={!!selectedJob || !!form.remoteJobId}
            disabled={syncPending}
          >
            <Select.TriggerContent>
              {syncPending
                ? "Syncing jobs…"
                : isLoading && !form.remoteJobId
                ? "Loading jobs…"
                : ((selectedJob ? jobLabel(selectedJob) : undefined) ??
                  (form.remoteJobId ? "Selected job" : "Select a job to import…"))}
            </Select.TriggerContent>
            <Select.Icon />
          </Select.Trigger>
          <Select.Content>
            <Select.Search
              value={jobSearch}
              onChange={(e) => setJobSearch(e.target.value)}
              placeholder="Search ATS jobs…"
              autoFocus
            />
            <Select.List viewportRef={jobsViewportRef}>
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Icon
                    name="IconLoadingCircle"
                    size={18}
                    className="animate-spin text-contrast/40"
                  />
                </div>
              ) : jobs.length === 0 && !syncPending ? (
                <div className="px-2 py-1.5 text-sm text-contrast/50">
                  {jobSearch.trim()
                    ? "No matching jobs."
                    : "No jobs found in this ATS."}
                </div>
              ) : (
                <>
                  {jobs.map((j, i) => {
                    const selectable = isRemoteJobSelectable(
                      j.status,
                      j.already_linked,
                    );
                    const closedBadge = remoteJobClosedBadge(j.status);
                    return (
                      <Select.Item
                        key={j.remote_job_id}
                        index={i}
                        selected={j.remote_job_id === form.remoteJobId}
                        disabled={!selectable}
                        onSelect={() => {
                          if (!selectable) return;
                          setSelectedJobMeta({
                            title: j.title,
                            remote_job_id: j.remote_job_id,
                          });
                          updateForm("remoteJobId", j.remote_job_id);
                          updateForm("description", j.description ?? "");
                          setJobOpen(false);
                        }}
                      >
                        <Select.ItemLabel>
                          {jobLabel(j)}
                          {j.already_linked
                            ? " · already imported"
                            : closedBadge
                              ? ` · ${closedBadge}`
                              : ""}
                        </Select.ItemLabel>
                        {j.remote_job_id === form.remoteJobId && (
                          <Select.ItemCheck />
                        )}
                      </Select.Item>
                    );
                  })}
                  {isFetchingNextPage && (
                    <div className="flex items-center justify-center py-2">
                      <Icon
                        name="IconLoadingCircle"
                        size={16}
                        className="animate-spin text-contrast/40"
                      />
                    </div>
                  )}
                </>
              )}
            </Select.List>
          </Select.Content>
        </Select.Root>
      </div>
    </div>
  );
}
